import { Box, Dialog, Grid, InputAdornment, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import styles from "../../ExpenseRequest/ExpenseRequest.module.scss";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomRequiredInputLabel,
  CustomTextFeild,
} from "../../../common/StyledComponents/StyledFields";
import { useReactToPrint } from "react-to-print";
import {
  apiRequest,
  catchErrorMessageHandler,
  displayDateFormat,
} from "../../../../infrastructure/backendService";
import toast from "react-hot-toast";
import ErrorPopup from "../../../common/ErrorPopup";
import apiPath from "../../../../config/apiPaths.json";
import APILoading from "../../../common/APILoading/APILoading";
import { useFormik } from "formik";
import { number, object, string } from "yup";
import { AttachMoney } from "@mui/icons-material";
import CustomDatePickerField from "../../../common/DatePicker/DatePicker";
import dayjs from "dayjs";
import CustomSelectField from "../../../common/CustomSelectField/CustomSelectField";
import ButtonLoader from "../../../common/ButtonLoader/ButtonLoader";
import SuccessPopup from "../../../devotee/donation/successPopup";

type viewExpenseRequestType = {
  handleClose: () => void;
  open: boolean;
  referenceNumber: string | number;
  renderWithoutDialog?: boolean;
  isApprovedExpense?: boolean;
  viewRequest?: boolean;
};

const textFieldStyles = {
  textField: {
    border: "1px solid rgba(136, 46, 20, 0.4)",
    backgroundColor: "#FFF9E4",
    borderRadius: "8px",
  },
  errorTextField: {
    border: "1px solid #f05802",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFF9E4",
  },
};

const ViewExpenseRequest = (props: viewExpenseRequestType) => {
  const {
    handleClose,
    open,
    referenceNumber,
    renderWithoutDialog,
    isApprovedExpense,
    viewRequest,
  } = props;

  const mailedBy = sessionStorage.getItem("name") ?? "";

  const componentRef = useRef<any>(null);

  const [loadingData, setLoadingData] = useState(false);

  const [expenseRequestData, setExpenseRequestData] = useState<any>(null);
  const [attachedFiles, setAttachedFiles] = useState<any[]>([]);
  const [statesList, setStatesList] = useState<any[]>([]);
  const [isUpdateFileLoading, setIsUpdateFileLoading] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [requestFiled, setRequestFiled] = useState(false);

  const initialValues = {
    mailToName: "",
    mailToAddress: "",
    mailToAddress2: "",
    mailToCity: "",
    mailToState: "",
    mailToZipcode: "",
    checkNumber: "",
    amountPaid: "",
    checkPostedBy: mailedBy,
    checkMailedDate: "",
  };

  const validationSchema = object().shape({
    mailToName: string().trim().required("Name is required"),
    mailToAddress: string().trim().required("Address is required"),
    mailToCity: string().trim().required("City is required"),
    mailToState: string().trim().required("State is required"),
    mailToZipcode: string()
      .trim()
      .required("Zipcode is required")
      .matches(/^\d{5}(-\d{4})?$/, "Invalid zipcode"),
    checkNumber: string().trim().required("Check number is required"),
    amountPaid: number()
      .required("Amount is required")
      .min(1, "Minimum amount must be $1"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      if (requestFiled) {
        setIsUpdateFileLoading(true);
      } else {
        setIsUpdateLoading(true);
      }
      updateApprovedRequest(values);
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    formik.setValues({
      ...formik.values,
      mailToName: expenseRequestData?.vendorDetails?.vendorName,
      mailToAddress: expenseRequestData?.vendorDetails?.address,
      mailToAddress2: expenseRequestData?.vendorDetails?.address2,
      mailToCity: expenseRequestData?.vendorDetails?.city,
      mailToState: expenseRequestData?.vendorDetails?.state,
      mailToZipcode: expenseRequestData?.vendorDetails?.zipcode?.replace(
        /\s+/g,
        ""
      ),
      checkNumber: expenseRequestData?.checkNumber,
      amountPaid: expenseRequestData?.budget,
      checkMailedDate: expenseRequestData?.checkMailedDate ?? dayjs(),
    });
  }, [expenseRequestData]);

  useEffect(() => {
    setAttachedFiles([]);
    if (referenceNumber) {
      setLoadingData(true);
      apiRequest({
        method: "GET",
        endpoint: `${apiPath.createExpenseDetails}/${referenceNumber}`,
        includeToken: true,
      })
        .then((res) => {
          setLoadingData(false);
          setExpenseRequestData(res?.data);
          if (res?.data?.expenseBills?.length > 0) {
            res?.data?.expenseBills?.forEach((receipt: any) => {
              fetchAttachedReceipts(receipt);
            });
          }
        })
        .catch((err) => {
          handleClose();
          setLoadingData(false);
          console.log(err);
          toast.custom(
            <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
          );
        });
    }
  }, [referenceNumber]);

  let receiptIds: any = [];

  const fetchAttachedReceipts = (receipt: any) => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.expenseBillsFile?.replace(
        "{expensebillsId}",
        receipt?.id
      ),
      includeToken: true,
      responseType: "blob",
    })
      .then((res) => {
        setAttachedFiles((prevState: any) => {
          if (!receiptIds?.includes(receipt?.id)) {
            const type = receipt?.billOrgFileName?.includes("pdf")
              ? "application/pdf"
              : "image/jpeg";
            const file = new Blob([res?.data], {
              type,
            });
            const url = URL.createObjectURL(file);
            return [...prevState, { file: url, id: receipt?.id, type }];
          } else {
            return prevState;
          }
        });
        setTimeout(() => {
          receiptIds?.push(receipt?.id);
        }, 0);
      })
      .catch((err) => {});
  };

  const fetchAllStatesList = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.findAllStates,
      includeToken: true,
      params: {
        size: 300,
        sortBy: "stateName",
      },
    })
      .then((res) => {
        setStatesList(res?.data?.content);
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const updateApprovedRequest = (values: any) => {
    const payload = {
      ...values,
      requestFiled,
      checkMailedDate: displayDateFormat(new Date(values?.checkMailedDate)),
    };
    if (referenceNumber) {
      apiRequest({
        method: "PUT",
        endpoint: `${apiPath.updateApprovedExpense}/${referenceNumber}`,
        includeToken: true,
        body: payload,
      })
        .then((res) => {
          setIsUpdateFileLoading(false);
          setIsUpdateLoading(false);
          handleClose();
          formik.resetForm();
          setRequestFiled(false);
          toast.custom(
            <SuccessPopup
              headerMessage="Success"
              showMessage={`Successfully updated the expense: ${referenceNumber}`}
            />
          );
        })
        .catch((err) => {
          toast.custom(
            <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
          );
          setIsUpdateFileLoading(false);
          setIsUpdateLoading(false);
        });
    }
  };

  useEffect(() => {
    fetchAllStatesList();
  }, []);

  const getTotalofReceipts = () => {
    let totalReceiptsAmount = 0;
    expenseRequestData?.expenseBills?.forEach((bill: any) => {
      totalReceiptsAmount += bill?.amount;
    });
    return totalReceiptsAmount?.toFixed(2);
  };

  const handleChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Expense_Request_${expenseRequestData?.referenceNumber}`,
  });

  const handleShowFile = (id: any) => {
    let fileURL = attachedFiles?.find((file: any) => file?.id === id)?.file;
    if (fileURL) {
      window.open(fileURL, "_blank", "noopener, noreferrer");
    } else {
      toast.custom(<ErrorPopup showMessage={"File does not exist"} />);
    }
  };

  const renderHeaderData = () => {
    return (
      <Box className={styles.header_container}>
        <div>
          <img
            src="/images/TempleOldLogo.png"
            alt="temple"
            className={styles.header_image}
          />
          <Typography className={styles.temple_name_data}>
            Shiva -Vishnu Temple
          </Typography>
        </div>
        <Box className={styles.temple_address}>
          <Typography className={styles.temple_address_data}>
            <span style={{ fontWeight: "bold", letterSpacing: "1px" }}>
              Hindu Community and Cultural Center
            </span>
            <br />A Registered Non-Profit Organization
            <br />
            Tax ID: 94-2427126, Inc: D08215891232,
            <br /> Arrowhead Ave, Livermore, CA 94551 <br />
            Tel: (925) 449-6255, Fax: (925) 455-0404.
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderSubmissionDetailsAndStatus = () => {
    return (
      <Box sx={{ mt: "10px" }}>
        <Box
          sx={{
            display: { xs: "block", sm: "flex" },
            justifyContent: { xs: "normal", sm: "space-between" },
            alignItems: { xs: "normal", sm: "center" },
          }}
        >
          <Box sx={{ display: "block" }}>
            <Typography className={styles.authorization_voucher_txt}>
              Expense Authorization Voucher
            </Typography>
            <Typography className={styles.authorization_voucher_desc_txt}>
              All requests for reimbursement must be submitted only through the
              respective Functional Committee Chairperson or President
            </Typography>
          </Box>
          {!isApprovedExpense && !viewRequest && (
            <CommonButton
              sx={{
                mt: { xs: "10px", sm: "0px" },
                height: "32px",
                alignItems: "center",
                fontSize: "13px",
              }}
              onClick={handlePrint}
              className="no_print"
            >
              Print
            </CommonButton>
          )}
        </Box>
        <Box sx={{ mt: "10px" }}>
          <Grid container columnSpacing={"8px"}>
            <Grid item xs={12} sm={6}>
              <Box
                className="common_box_shadow"
                sx={{ p: "10px !important", height: "calc(100% - 20px)" }}
              >
                <Typography className={styles.value_text}>
                  <span className={styles.header_text}>Submitted Date:</span>{" "}
                  {expenseRequestData?.submittedDate}
                </Typography>
                <Typography className={styles.value_text} sx={{ mt: "5px" }}>
                  <span className={styles.header_text}>Name:</span>{" "}
                  {expenseRequestData?.expenseEnteredBy}
                </Typography>
                <Typography className={styles.value_text} sx={{ mt: "5px" }}>
                  <span className={styles.header_text}>Short Title:</span>{" "}
                  {expenseRequestData?.shortTitle}
                </Typography>
                <Typography className={styles.value_text} sx={{ mt: "5px" }}>
                  <span className={styles.header_text}>
                    Devotee Sponsored?:
                  </span>{" "}
                  {expenseRequestData?.devoteeSponsored ? "Yes" : "No"}
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              mt={{ xs: "8px", sm: "0px" }}
            >
              <Box
                className="common_box_shadow"
                sx={{ p: "10px !important", height: "calc(100% - 20px)" }}
              >
                <Box>
                  <Typography className={styles.value_text}>
                    <span className={styles.header_text}>Reference No:</span>{" "}
                    {expenseRequestData?.referenceNumber}
                  </Typography>
                  <Typography className={styles.value_text} sx={{ mt: "5px" }}>
                    <span className={styles.header_text}>Year of Budget:</span>{" "}
                    {expenseRequestData?.yearOfBudget}
                  </Typography>
                  <Typography className={styles.value_text} sx={{ mt: "5px" }}>
                    <span className={styles.header_text}>Current Amount:</span>{" "}
                    ${expenseRequestData?.currentAmount?.toFixed(2)}
                  </Typography>
                </Box>
                <Typography className={styles.value_text} sx={{ mt: "5px" }}>
                  <span className={styles.header_text}>Status:</span>{" "}
                  {expenseRequestData?.status}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  const renderMainAccountAndSubAccountDetails = () => {
    return (
      <>
        <Box sx={{ mt: "8px" }}>
          <Grid container columnSpacing="8px">
            <Grid item xs={12} sm={6}>
              <Box
                className="common_box_shadow"
                sx={{ p: "10px !important", height: "calc(100% - 20px)" }}
              >
                <Typography className={styles.account_heading_text}>
                  Main Account
                </Typography>
                <Typography className={styles.value_text} mt="5px">
                  <span className={styles.header_text}>Account ID:</span>{" "}
                  {expenseRequestData?.accountId}
                </Typography>
                <Typography className={styles.value_text} mt="5px">
                  <span className={styles.header_text}>Account Name:</span>{" "}
                  {expenseRequestData?.accountName}
                </Typography>
                <Typography className={styles.value_text} mt="5px">
                  <span className={styles.header_text}>Budget:</span> $
                  {expenseRequestData?.budget?.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{ mt: { xs: "8px", sm: "0px" }, p: "10px !important" }}
                className="common_box_shadow"
              >
                <Typography className={styles.account_heading_text}>
                  Sub Account
                </Typography>
                <Typography className={styles.value_text} mt="5px">
                  <span className={styles.header_text}>Account ID:</span>{" "}
                  {expenseRequestData?.subAccountId}
                </Typography>
                <Typography className={styles.value_text} mt="5px">
                  <span className={styles.header_text}>Account Name:</span>{" "}
                  {expenseRequestData?.subAccountName}
                </Typography>
                <Typography className={styles.value_text} mt="5px">
                  <span className={styles.header_text}>Budget:</span> $
                  {expenseRequestData?.subBudget?.toFixed(2)}
                </Typography>
                <Typography className={styles.value_text} mt="5px">
                  <span className={styles.header_text}>
                    Total Approved Expenses:
                  </span>{" "}
                  ${expenseRequestData?.totalApprovedExpenses?.toFixed(2)}
                </Typography>
                <Typography className={styles.value_text} mt="5px">
                  <span className={styles.header_text}>
                    Total Expenses Spent to date:
                  </span>{" "}
                  ${expenseRequestData?.totalExpensesRequested?.toFixed(2)}
                </Typography>
                <Typography className={styles.value_text} mt="5px">
                  <span className={styles.header_text}>Remaining Balance:</span>{" "}
                  ($
                  {Math.abs(expenseRequestData?.remainingBalance)?.toFixed(2)})
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Typography className={styles.value_text} sx={{ mt: "10px" }}>
          <span className={styles.header_text}>Current Expense Details:</span>{" "}
          {expenseRequestData?.currentExpenseDetails}
        </Typography>
      </>
    );
  };

  const renderMailCheckFields = () => {
    return (
      <Box sx={{ maxWidth: "320px" }}>
        {CustomRequiredInputLabel("Name", true)}
        <CustomTextFeild
          fullWidth
          required
          value={formik.values.mailToName}
          sx={
            formik.touched.mailToName && formik?.errors?.mailToName
              ? textFieldStyles.errorTextField
              : textFieldStyles.textField
          }
          onChange={formik.handleChange}
          name="mailToName"
          placeholder="Name"
        />
        {formik.touched.mailToName && formik?.errors?.mailToName && (
          <Typography sx={{ color: "red", fontSize: "12px" }}>
            {formik?.errors?.mailToName}
          </Typography>
        )}
        <Box sx={{ mt: "8px" }} />
        {CustomRequiredInputLabel("Address", true)}
        <CustomTextFeild
          fullWidth
          required
          value={formik.values.mailToAddress}
          sx={textFieldStyles.textField}
          onChange={formik.handleChange}
          name="mailToAddress"
          placeholder="Enter address"
        />
        {formik.touched.mailToAddress && formik?.errors?.mailToAddress ? (
          <Typography sx={{ color: "red", fontSize: "12px" }}>
            {formik?.errors?.mailToAddress}
          </Typography>
        ) : (
          ""
        )}
        <Box sx={{ mt: "8px" }} />
        {CustomRequiredInputLabel("Address 2", false)}
        <CustomTextFeild
          fullWidth
          required
          value={formik.values.mailToAddress2}
          sx={textFieldStyles.textField}
          onChange={formik.handleChange}
          name="mailToAddress2"
          placeholder="Enter address 2"
        />
        <Box sx={{ mt: "8px" }} />
        {CustomRequiredInputLabel("City", true)}
        <CustomTextFeild
          fullWidth
          required
          value={formik.values.mailToCity}
          sx={{
            ...textFieldStyles.textField,
          }}
          onChange={formik.handleChange}
          name="mailToCity"
          placeholder="Enter city"
        />
        {formik.touched.mailToCity && formik?.errors?.mailToCity ? (
          <Typography sx={{ color: "red", fontSize: "12px" }}>
            {formik?.errors?.mailToCity}
          </Typography>
        ) : (
          ""
        )}
        <Box sx={{ mt: "8px" }} />
        {CustomRequiredInputLabel("State", true)}
        <CustomSelectField
          handleDropdownChange={handleChange}
          errorMessage={
            formik.touched.mailToState ? formik?.errors?.mailToState : ""
          }
          dropDownList={statesList}
          value={formik.values.mailToState}
          fieldName="mailToState"
          placeholder="Select state"
          optionLabel="stateName"
          optionValue="stateCode"
        />
        <Box sx={{ mt: "8px" }} />
        {CustomRequiredInputLabel("Zipcode", true)}
        <CustomTextFeild
          fullWidth
          required
          value={formik.values.mailToZipcode}
          sx={textFieldStyles.textField}
          onChange={(e) => {
            const numericValue = e.target.value
              .replace(/[^\d-]/g, "")
              .slice(0, 10);
            formik.setFieldValue("mailToZipcode", numericValue);
          }}
          name="mailToZipcode"
          placeholder="Enter zipcode"
        />
        {formik.touched.mailToZipcode && formik?.errors?.mailToZipcode && (
          <Typography sx={{ color: "red", fontSize: "12px" }}>
            {formik?.errors?.mailToZipcode}
          </Typography>
        )}
      </Box>
    );
  };

  const renderMailCheckData = () => {
    return (
      <>
        {isApprovedExpense ? (
          renderMailCheckFields()
        ) : (
          <Typography className={styles.value_text}>
            <span className={styles.header_text}>Mail Check to:</span> <br />
            <span
              className={styles.value_text}
              dangerouslySetInnerHTML={{
                __html: expenseRequestData?.mailCheckTo?.replace(
                  /\n/g,
                  "<br />"
                ),
              }}
            />
          </Typography>
        )}
      </>
    );
  };

  const renderCheckDetailFields = () => {
    return (
      <Box sx={{ maxWidth: "320px" }}>
        {CustomRequiredInputLabel("Check Number", true)}
        <CustomTextFeild
          fullWidth
          required
          value={formik.values.checkNumber}
          sx={{
            ...textFieldStyles.textField,
          }}
          onChange={(e) => {
            const numericValue = e.target.value
              .replace(/[^\d]/g, "")
              .slice(0, 20);
            formik.setFieldValue("checkNumber", numericValue);
          }}
          name="checkNumber"
          placeholder="Enter check number"
        />
        {formik.touched.checkNumber && formik?.errors?.checkNumber ? (
          <Typography sx={{ color: "red", fontSize: "12px" }}>
            {formik?.errors?.checkNumber}
          </Typography>
        ) : (
          ""
        )}
        <Box sx={{ mt: "8px" }} />
        {CustomRequiredInputLabel("Amount", true)}
        <CustomTextFeild
          fullWidth
          required
          name="amountPaid"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AttachMoney fontSize="small" sx={{ position: "absolute" }} />
              </InputAdornment>
            ),
          }}
          value={formik.values.amountPaid}
          placeholder="Enter amount"
          onChange={(e) => {
            const numericValue = e.target.value
              .replace(/[^\d.]/g, "")
              .slice(0, 15); // Allow only the first 10 numbers
            formik.setFieldValue("amountPaid", numericValue);
          }}
          sx={
            formik.touched.amountPaid && formik.errors.amountPaid
              ? textFieldStyles.errorTextField
              : textFieldStyles.textField
          }
        />
        {formik.touched.amountPaid && formik?.errors?.amountPaid && (
          <Typography sx={{ color: "red", fontSize: "12px" }}>
            {formik?.errors?.amountPaid}
          </Typography>
        )}
        <Box sx={{ mt: "8px" }} />
        {CustomRequiredInputLabel("Mailed Date", true)}
        <CustomDatePickerField
          date={formik.values.checkMailedDate}
          handleDateChange={handleChange}
          errorMessage={
            formik.touched.checkMailedDate ? formik.errors.checkMailedDate : ""
          }
          name="checkMailedDate"
        />
        <Box sx={{ mt: "8px" }} />
        {CustomRequiredInputLabel("Mailed By", true)}
        <CustomTextFeild
          fullWidth
          required
          value={formik.values.checkPostedBy}
          sx={{
            ...textFieldStyles.textField,
            "& .MuiInputBase-input": { cursor: "not-allowed" },
          }}
          onChange={formik.handleChange}
          name="checkPostedBy"
          disabled
        />
      </Box>
    );
  };

  const renderCheckDetails = () => {
    return (
      <Box>
        {isApprovedExpense ? (
          renderCheckDetailFields()
        ) : (
          <>
            <Typography className={styles.value_text}>
              <span className={styles.header_text}>Check Number:</span>{" "}
              {expenseRequestData?.checkNumber}
            </Typography>
            <Typography className={styles.value_text} mt="5px">
              <span className={styles.header_text}>Amount:</span>{" "}
              {expenseRequestData?.budget
                ? `$${expenseRequestData?.budget?.toFixed(2)}`
                : ""}
            </Typography>
            <Typography className={styles.value_text} mt="5px">
              <span className={styles.header_text}>Mailed Date:</span>{" "}
              {expenseRequestData?.checkMailedDate}
            </Typography>
            <Typography className={styles.value_text} mt="5px">
              <span className={styles.header_text}>Mailed By:</span>{" "}
              {expenseRequestData?.mailedBy}
            </Typography>
          </>
        )}
      </Box>
    );
  };

  const renderReceiptsTable = () => {
    return (
      <>
        {/* {expenseRequestData?.receiptRequired && ( */}
        <Box sx={{ mt: "10px" }}>
          {expenseRequestData?.expenseBills?.length > 0 ? (
            <>
              <Typography className={styles.header_text} sx={{ mb: "5px" }}>
                Receipts Attached:
              </Typography>
              <table
                className={styles.table_container}
                style={{ borderRadius: "0px" }}
              >
                <thead>
                  <th>File Name</th>
                  <th>Amount</th>
                  <th>Description</th>
                  <th>Uploaded on</th>
                </thead>
                <tbody>
                  <>
                    {expenseRequestData?.expenseBills?.map?.(
                      (row: any, index: number) => (
                        <tr key={index}>
                          <td>
                            <Typography
                              sx={{ textDecoration: "underline" }}
                              onClick={() => handleShowFile(row?.id)}
                              className="print_remove_link"
                            >
                              {row?.billOrgFileName}
                            </Typography>
                          </td>
                          <td className={styles.amount_text} width={`48px`}>
                            ${row?.amount?.toFixed(2)}
                          </td>
                          <td className={styles.table_desc_txt}>
                            {row?.description}
                          </td>
                          <td>{row?.enteredOn}</td>
                        </tr>
                      )
                    )}
                    <tr className={styles.table_row_background}>
                      <td className={styles.header_text}>Total</td>
                      <td className={styles.amount_text}>
                        ${getTotalofReceipts()}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </>
                </tbody>
              </table>
            </>
          ) : (
            <>
              {expenseRequestData?.receiptRequired && (
                <Typography
                  className={styles.header_text}
                  sx={{ mb: "5px", color: "red !important" }}
                >
                  Receipts Pending.
                </Typography>
              )}
            </>
          )}
        </Box>
        {/* )} */}
      </>
    );
  };

  const renderAuthorizationsAndReceipts = () => {
    return (
      <Box sx={{ mt: "5px" }}>
        <Typography
          className={styles.account_heading_text}
        >
          Authorizations
        </Typography>
        <Box
          className="common_box_shadow"
          sx={{ mt: "5px", p: "10px !important" }}
        >
          <Grid container columnSpacing={"8px"}>
            <Grid item xs={12} sm={6}>
              <Typography className={styles.value_text}>
                {expenseRequestData?.notAuthorized &&
                expenseRequestData?.notAuthorizedCategory?.toLowerCase() ===
                  "committeechair" ? (
                  <>
                    <Box component={"span"} sx={{ color: "red" }}>
                      Not Authorized on {expenseRequestData?.notAuthorizedOn}
                    </Box>
                    <br />
                    {expenseRequestData?.notAuthorizedByFullName},{" "}
                  </>
                ) : (
                  <>
                    {expenseRequestData?.committeeChairAuthorized && (
                      <>
                        Authorized on{" "}
                        {expenseRequestData?.committeeChairAuthorizedDate}{" "}
                        <br />{" "}
                        {expenseRequestData?.committeeChairAuthorizedFullname},{" "}
                      </>
                    )}
                  </>
                )}
                <span className={styles.header_text}>
                  {" "}
                  Committee Chairperson
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mt: { xs: "5px", sm: "0px" } }}>
              <Typography className={styles.value_text}>
                {expenseRequestData?.notAuthorized &&
                expenseRequestData?.notAuthorizedCategory?.toLowerCase() ===
                  "treasurer" ? (
                  <>
                    <Box component={"span"} sx={{ color: "red" }}>
                      Not Authorized on {expenseRequestData?.notAuthorizedOn}
                    </Box>
                    <br />
                    {expenseRequestData?.notAuthorizedByFullName},{" "}
                  </>
                ) : (
                  <>
                    {expenseRequestData?.treasurerAuthorized && (
                      <>
                        Authorized on{" "}
                        {expenseRequestData?.treasurerAuthorizedDate} <br />{" "}
                        {expenseRequestData?.treasurerAuthorizedFullname},{" "}
                      </>
                    )}
                  </>
                )}
                <span className={styles.header_text}>Treasurer</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mt: "5px" }}>
              <Typography className={styles.value_text}>
                {expenseRequestData?.notAuthorized &&
                expenseRequestData?.notAuthorizedCategory?.toLowerCase() ===
                  "president" ? (
                  <>
                    <Box component={"span"} sx={{ color: "red" }}>
                      Not Authorized on {expenseRequestData?.notAuthorizedOn}
                    </Box>
                    <br />
                    {expenseRequestData?.notAuthorizedByFullName},{" "}
                  </>
                ) : (
                  <>
                    {expenseRequestData?.presidentAuthorized && (
                      <>
                        Authorized on{" "}
                        {expenseRequestData?.presidentAuthorizedDate} <br />{" "}
                        {expenseRequestData?.presidentAuthorizedFullname},{" "}
                      </>
                    )}
                  </>
                )}
                <span className={styles.header_text}>President</span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mt: "5px" }}>
              <Typography className={styles.value_text}>
                {expenseRequestData?.notAuthorized &&
                expenseRequestData?.notAuthorizedCategory?.toLowerCase() ===
                  "chairman" ? (
                  <>
                    <Box component={"span"} sx={{ color: "red" }}>
                      Not Authorized on {expenseRequestData?.notAuthorizedOn}
                    </Box>
                    <br />
                    {expenseRequestData?.notAuthorizedByFullName},{" "}
                  </>
                ) : (
                  <>
                    {expenseRequestData?.chairmanAuthorized && (
                      <>
                        Authorized on{" "}
                        {expenseRequestData?.chairmanAuthorizedDate} <br />{" "}
                        {expenseRequestData?.chairmanAuthorizedFullname},{" "}
                      </>
                    )}
                  </>
                )}
                <span className={styles.header_text}>Chairman</span>
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: "8px" }}>
          {isApprovedExpense && (
            <Typography
              className={styles.account_heading_text}
              sx={{ mb: "5px" }}
            >
              Mail Check to
            </Typography>
          )}
          <Grid container columnSpacing={"8px"}>
            <Grid item xs={12} sm={6}>
              <Box
                className="common_box_shadow"
                sx={{ p: "10px !important", height: "calc(100% - 20px)" }}
              >
                {renderMailCheckData()}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} mt={{ xs: "8px", sm: "0px" }}>
              <Box
                className="common_box_shadow"
                sx={{
                  p: "10px !important",
                  height: "calc(100% - 20px)",
                }}
              >
                {renderCheckDetails()}
                {isApprovedExpense && renderButtonsComponent()}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  const renderComments = () => {
    return (
      <Box
        className="common_box_shadow"
        sx={{ mt: "8px", p: "10px !important" }}
      >
        <Typography className={styles.header_text}>
          Reference Number: {referenceNumber}
        </Typography>
        {expenseRequestData?.expenseComments?.map((comment: any) => (
          <Box key={comment?.id} sx={{ mt: "8px" }}>
            <Typography className={styles.header_text}>
              Comments added by: {comment?.addedByFullName} on{" "}
              {comment?.addedOn}
            </Typography>
            <Typography
              className={styles.header_text}
              dangerouslySetInnerHTML={{ __html: comment?.comments }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const renderButtonsComponent = () => {
    return (
      <Box
        sx={{ mt: "10px", display: "flex", flexWrap: "wrap", rowGap: "10px" }}
        className="no_print"
      >
        {isApprovedExpense && (
          <>
            <CommonButton
              onClick={() => {
                setTimeout(() => {
                  formik.handleSubmit();
                }, 100);
                setRequestFiled(true);
              }}
              sx={{ mr: "10px" }}
            >
              <ButtonLoader
                isBtnLoading={isUpdateFileLoading}
                btnText="Update and File"
              />
            </CommonButton>
            <CommonButton
              onClick={() => formik.handleSubmit()}
              sx={{ mr: "10px" }}
            >
              <ButtonLoader isBtnLoading={isUpdateLoading} btnText="Update" />
            </CommonButton>
          </>
        )}
        <CommonButton onClick={handleClose}>Close</CommonButton>
      </Box>
    );
  };

  const renderReceiptImages = () => {
    return (
      <>
        {attachedFiles?.length > 0 ? (
          <Box sx={{ mt: "20px" }}>
            {attachedFiles?.map((fileData: any) => (
              <>
                {fileData?.type === "application/pdf" ? (
                  <iframe
                    src={fileData?.file}
                    width="100%"
                    height={"500px"}
                    key={fileData?.id}
                    title={fileData?.id}
                    className="no_print"
                  />
                ) : (
                  <Box
                    component={"img"}
                    src={fileData?.file}
                    alt="receipt"
                    sx={{ width: "100%" }}
                  />
                )}
              </>
            ))}
          </Box>
        ) : (
          <>
            {expenseRequestData?.expenseBills?.length > 0 && (
              <Typography
                className={styles.header_text}
                sx={{ my: "5px", color: "red !important" }}
              >
                File does not exist.
              </Typography>
            )}
          </>
        )}
      </>
    );
  };

  const renderNoDataFound = () => {
    return (
      <Box>
        <Typography className="sub_heading" sx={{ color: "red !important" }}>
          Incorrect Reference Number.
        </Typography>
        {!renderWithoutDialog && !isApprovedExpense && renderButtonsComponent()}
      </Box>
    );
  };

  const renderDetails = () => {
    return (
      <Box
        ref={componentRef}
        className={styles.view_expense_request_container}
        sx={{
          background: "#FFF9E4",
          p: {
            xs: "10px",
            sm: "25px",
            md: `${renderWithoutDialog ? "25px" : "40px"}`,
          },
        }}
      >
        {!loadingData ? (
          <>
            {expenseRequestData ? (
              <>
                {renderHeaderData()}
                {renderSubmissionDetailsAndStatus()}
                {renderMainAccountAndSubAccountDetails()}
                {renderAuthorizationsAndReceipts()}
                {renderReceiptsTable()}
                {expenseRequestData?.expenseComments?.length > 0 &&
                  renderComments()}
                {!isApprovedExpense && !viewRequest && renderReceiptImages()}
                {!renderWithoutDialog &&
                  !isApprovedExpense &&
                  renderButtonsComponent()}
              </>
            ) : (
              <>{renderNoDataFound()}</>
            )}
          </>
        ) : (
          <APILoading />
        )}
      </Box>
    );
  };

  const render = () => {
    return (
      <>
        {renderWithoutDialog ? (
          renderDetails()
        ) : (
          <Dialog
            onClose={handleClose}
            open={open}
            PaperProps={{
              sx: {
                width: { xs: "100%", md: "90%" },
                maxWidth: { xs: "100%", md: "1200px" },
                maxHeight: {
                  xs: "calc(100% - 40px)",
                  sm: "calc(100% - 80px)",
                  md: "calc(100% - 120px)",
                },
              },
            }}
          >
            {renderDetails()}
          </Dialog>
        )}
      </>
    );
  };

  return <>{render()}</>;
};

export default ViewExpenseRequest;
